// Card types the parser recognises inside ```<type> fences. Internal types are only rendered for
// the built-in agent; extension agents get the other list.
const INTERNAL_CARD_TYPES = ["mention", "section"];
const EXTENSION_CARD_TYPES = [
  "metric_card",
  "action_list",
  "status_table",
  "alert_banner",
  "progress_indicator",
  "timeline",
  "collapsible",
  "comparison",
  "code_diff",
  "link_card",
  "callout",
];

const CARD_TYPE_NAMES = [...INTERNAL_CARD_TYPES, ...EXTENSION_CARD_TYPES];

const COMPLETE_BLOCK_REGEX = new RegExp(
  "```(" + CARD_TYPE_NAMES.join("|") + ")\\n([\\s\\S]*?)\\n```",
  "g"
);

const INCOMPLETE_BLOCK_REGEX = new RegExp(
  "```(" + CARD_TYPE_NAMES.join("|") + ")\\n([\\s\\S]*)$"
);

function tryParseJson(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function repairAndParse(fragment) {
  if (!fragment || !fragment.trim()) return null;

  const direct = tryParseJson(fragment);
  if (direct) return direct;

  let repaired = fragment.trim();
  const quoteCount = (repaired.match(/(?<!\\)"/g) || []).length;
  if (quoteCount % 2 !== 0) repaired += '"';

  const closers = [
    "}", "]", "]}", "]}",
    '"}', '"]', '"]}', '"}]',
    "}]}", "]}}", '"}]}',
  ];
  for (const closer of closers) {
    const parsed = tryParseJson(repaired + closer);
    if (parsed && typeof parsed === "object") return parsed;
  }

  return extractFieldsWithRegex(fragment);
}

function extractFieldsWithRegex(fragment) {
  const result = {};

  const stringFields = [...fragment.matchAll(/"(\w+)"\s*:\s*"((?:[^"\\]|\\.)*)"/g)];
  for (const [, key, value] of stringFields) {
    result[key] = value;
  }

  const arrayMatch = fragment.match(/"content"\s*:\s*\[([\s\S]*)/);
  if (arrayMatch) {
    const headings = [...fragment.matchAll(/"heading"\s*:\s*"((?:[^"\\]|\\.)*)"/g)].map(m => m[1]);
    const bodies = [...fragment.matchAll(/"body"\s*:\s*"((?:[^"\\]|\\.)*)"/g)].map(m => m[1]);
    if (headings.length > 0) {
      result.content = headings.map((h, i) => ({
        heading: h,
        body: bodies[i] || "",
      }));
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function parseCustomBlocks(text, { isInternal = true } = {}) {
  const segments = [];
  let lastIndex = 0;

  const completeMatches = [...text.matchAll(COMPLETE_BLOCK_REGEX)];

  for (const match of completeMatches) {
    const before = text.slice(lastIndex, match.index).trim();
    if (before) {
      segments.push({ type: "text", content: before });
    }
    const cardType = match[1];
    const isInternalCard = INTERNAL_CARD_TYPES.includes(cardType);
    // repairAndParse tries a plain JSON.parse first.
    const data = isInternal === isInternalCard && repairAndParse(match[2]);
    segments.push(
      isInternal !== isInternalCard ? { type: "text", content: match[2].trim() || match[0] }
        : data ? { type: "card", cardType, data, isStreaming: false }
        : { type: "text", content: match[0] }
    );
    lastIndex = match.index + match[0].length;
  }

  const remaining = text.slice(lastIndex);

  let incompleteBlock = null;
  const incompleteMatch = remaining.match(INCOMPLETE_BLOCK_REGEX);
  if (incompleteMatch) {
    const before = remaining.slice(0, incompleteMatch.index).trim();
    if (before) {
      segments.push({ type: "text", content: before });
    }
    const cardType = incompleteMatch[1];
    const isInternalCard = INTERNAL_CARD_TYPES.includes(cardType);
    if (isInternal !== isInternalCard) {
      const blockRaw = remaining.slice(incompleteMatch.index).trim();
      if (blockRaw) segments.push({ type: "text", content: blockRaw });
    } else {
      incompleteBlock = { type: "card", cardType, data: repairAndParse(incompleteMatch[2]) || {}, isStreaming: true };
    }
  } else {
    const trailing = remaining.trim();
    if (trailing) {
      segments.push({ type: "text", content: trailing });
    }
  }

  return { segments, incompleteBlock };
}
